<script setup lang="ts">
import { computed, onMounted, ref } from "vue"
import { useI18n } from "vue-i18n"
import { useRoute } from "vue-router"

import Barcode from "@/components/shared/Barcode.vue"
import Button from "@/components/shared/Button.vue"
import router from "@/plugins/router"
import store from "@/plugins/store"
import purchaseProductService from "@/services/purchase-product"
import type { Partner } from "@/types/partner.type"
import type { PurchaseProduct } from "@/types/purchase-product.type"
import { reportSentry } from "@/utils/sentry"
import { unitedNumber } from "@/utils/string"

const route = useRoute()
const { t } = useI18n()

const partner = computed<Partner | null>(() => store.getters.partner)

const purProductId = computed(() => route.params.pur_product_id)
const purProduct = ref<PurchaseProduct | null>(null)
const cutCount = computed(() => Number(route.query.cutCount))

async function loadProduct() {
  const data = await purchaseProductService.get(Number(purProductId.value))
  purProduct.value = data
}

onMounted(async () => {
  try {
    await loadProduct()
  } catch (error) {
    console.log(error)
    reportSentry(error)
    router.replace("/ship_outs")
  }
})

const print = () => window.print()
</script>

<template>
  <div v-if="partner && purProduct" class="d-flex flex-column align-items-center container">
    <div class="d-flex flex-column mb-5 p-5 guide">
      <div class="guide-text">{{ t("shipOut.guides.print") }}</div>
      <div class="d-flex justify-content-center mt-3 gap-3">
        <Button :text="t('shared.print')" color="warning" @click="print" />
        <Button type="link" :text="t('shared.complete')" color="success" path="/ship_outs" />
      </div>
    </div>
    <div v-for="idx in cutCount" class="d-flex flex-column a4-box" :key="idx">
      <div class="row info-row">
        <div class="col-4 info-key">{{ t("order.title") }}</div>
        <div class="col-8 info-value">
          {{ partner.supplierType !== "independent" ? purProduct.purchase.code : purProduct.purchase.orderCode }}
        </div>
      </div>
      <div v-if="partner.supplierType !== 'independent'" class="row info-row">
        <div class="col-4 info-key">{{ t("item.title") }}</div>
        <div class="col-8 info-value">{{ purProduct.product.quality.name }}</div>
      </div>
      <div class="row info-row">
        <div class="col-4 info-key">{{ t("item.code") }}</div>
        <div class="col-8 info-value">{{ purProduct.product.quality.code }}</div>
      </div>
      <div class="row info-row">
        <div class="col-4 info-key">{{ t("colorProduct.colorCode") }}</div>
        <div v-if="purProduct.salesType === 'standard_order'" class="col-8 info-value">
          {{ purProduct.product.colorCode }}
        </div>
        <div v-if="purProduct.salesType === 'print'" class="col-8 info-value">
          {{ t("orderItem.salesTypeList.print") }}
        </div>
      </div>
      <div class="row info-row">
        <div class="col-4 info-key">{{ t("order.quantity") }}</div>
        <div class="col-8 info-value">{{ unitedNumber(purProduct.quantity, t("item.units.yards_short")) }}</div>
      </div>
      <div v-if="partner.supplierType !== 'independent'" class="d-flex flex-column align-items-center my-auto">
        <div class="barcode">
          <Barcode :value="purProduct.id" />
        </div>
        <div class="mt-2 barcode-value">
          {{ purProduct.id }}
        </div>
      </div>
      <div class="d-flex justify-content-center">
        <div class="count-index">{{ idx }} / {{ cutCount }}</div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.guide-text {
  font-size: 16px;
}

.info-row {
  font-size: 36px;

  .info-key {
    font-weight: bold;
  }
}

.info-row + .info-row {
  margin-top: 2mm;
}

.barcode {
  width: 300px;
}

.barcode-value {
  font-size: 16px;
  font-weight: bold;
}

.count-index {
  font-size: 48px;
  font-weight: bold;
}

/* A4 portrait ratio box */
.a4-box {
  /* Screen: keep A4 aspect ratio and reasonable width */
  width: 190mm;
  aspect-ratio: 210 / 297;
  /* A4 = 210mm x 297mm */
  border: 1px solid #dee2e6;
  /* screen-only border */
  box-sizing: border-box;
  margin-bottom: 40px;
  padding: 10mm;
}

/* Print rules */
@media print {
  @page {
    size: A4 portrait;
    /* ensure A4 paper */
    margin: 0;
    /* adjust if you want printer margins */
  }

  .a4-box {
    width: 190mm;
    height: 276mm;
    /* a little bit smaller than A4 size on paper */
    margin-top: 10mm;
    margin-bottom: 0;

    break-inside: avoid;
    page-break-inside: avoid;

    aspect-ratio: auto;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  /* Add a page break BEFORE every subsequent box—not after the last one */
  .a4-box + .a4-box {
    break-before: page;
    page-break-before: always;
    /* legacy fallback */
  }

  .guide {
    display: none !important;
  }
}

/* Optional: fallback for very old browsers without aspect-ratio */
@supports not (aspect-ratio: 1) {
  .a4-box {
    position: relative;
    height: auto;
  }

  .a4-box::before {
    content: "";
    display: block;
    padding-top: calc(297 / 210 * 100%);
    /* ≈141.4286% */
  }

  .a4-box > * {
    position: absolute;
    inset: 0;
  }
}
</style>
